import React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import ServySparkle from "./ServySparkle";
import servyDuck from "../../assets/images/servy/servy-duck.png";
import { hostChatPath, servyChatPath } from "../../chat/chatRoutes";
import { hasServyKnowledgeBase } from "../../chat/servyKnowledgeBase";
import { ShowHomeMenu } from "../../API/HomeMenuAPI";
import "./ChatOptions.scss";

/**
 * Chat tab: choose between asking Servy and the existing host chat. Servy is
 * offered only once the unit details confirm uploaded guide files; without
 * them (or when the lookup fails) there is nothing to choose, so the guest
 * goes straight into the host chat.
 */
const ChatOptions = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { room_uuid } = useParams();
  const lang = sessionStorage.getItem("lang") || i18n.language || "EN";
  const isArabic = String(lang).toUpperCase() === "AR";
  const {
    data: unitRes,
    isSuccess: unitLoaded,
    isError: unitLookupFailed,
  } = ShowHomeMenu(room_uuid, lang);
  const showServy = hasServyKnowledgeBase(unitRes?.data?.data);

  const open = (path, value) => {
    ReactGA.event({ category: "Chat options", action: "Click", value });
    navigate(path);
  };

  if (!showServy) {
    // Wait for the unit details rather than flash a chooser we may skip.
    return unitLoaded || unitLookupFailed ? (
      <Navigate to={hostChatPath(room_uuid)} replace />
    ) : null;
  }

  return (
    <div className={`chat-options${isArabic ? " arabic" : ""}`}>
      <div className="chat-options__title">{t("Chat")}</div>
      <p className="chat-options__subtitle">{t("How can we help?")}</p>

      <section className="chat-options__card chat-options__card--servy">
        <img src={servyDuck} alt="" className="chat-options__duck" />
        <div className="chat-options__card-title">{t("Ask Servy")}</div>
        <p className="chat-options__card-text">
          {t("Get instant answers about your stay.")}
        </p>
        <button
          type="button"
          className="chat-options__button chat-options__button--servy"
          onClick={() => open(servyChatPath(room_uuid), "servy-chat")}
        >
          <ServySparkle variant="gold" size={16} />
          {t("Talk to Servy")}
        </button>
      </section>

      <section className="chat-options__card chat-options__card--host">
        <div className="chat-options__host">
          <span className="chat-options__host-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M17 2H7C4.24 2 2 4.23 2 6.98V12.96V13.96C2 16.71 4.24 18.94 7 18.94H8.5C8.77 18.94 9.13 19.12 9.3 19.34L10.8 21.33C11.46 22.21 12.54 22.21 13.2 21.33L14.7 19.34C14.89 19.09 15.19 18.94 15.5 18.94H17C19.76 18.94 22 16.71 22 13.96V6.98C22 4.23 19.76 2 17 2ZM8 12C7.44 12 7 11.55 7 11C7 10.45 7.45 10 8 10C8.55 10 9 10.45 9 11C9 11.55 8.56 12 8 12ZM12 12C11.44 12 11 11.55 11 11C11 10.45 11.45 10 12 10C12.55 10 13 10.45 13 11C13 11.55 12.56 12 12 12ZM16 12C15.44 12 15 11.55 15 11C15 10.45 15.45 10 16 10C16.55 10 17 10.45 17 11C17 11.55 16.56 12 16 12Z"
                fill="currentColor"
              />
            </svg>
          </span>
          <div>
            <div className="chat-options__card-title">
              {t("Chat with your Host")}
            </div>
            <p className="chat-options__card-text">
              {t("Talk directly with your host.")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="chat-options__button chat-options__button--host"
          onClick={() => open(hostChatPath(room_uuid), "host-chat")}
        >
          {t("Open Chat")}
        </button>
      </section>

      <p className="chat-options__note">
        {t("Servy is available 24/7.")}
        <br />
        {t("Host replies during active hours")}
      </p>
    </div>
  );
};

export default ChatOptions;
